// Wishlist — localStorage-based for Shopify new customer accounts

const Wishlist = (() => {
  const LS_WL_KEY = 'wishlist_items';

  function productGid(id) {
    if (!id) return '';
    return id.toString().startsWith('gid://') ? id.toString() : `gid://shopify/Product/${id}`;
  }

  function normalizeItem(item) {
    if (!item) return null;

    if (typeof item === 'string') {
      return {
        id: item,
        gid: productGid(item),
        handle: '',
        title: '',
        url: '',
        image: '',
        price: ''
      };
    }

    const gid = item.gid || item.id || item.productGid || '';

    return {
      id: productGid(gid),
      gid: productGid(gid),
      handle: item.handle || '',
      title: item.title || '',
      url: item.url || (item.handle ? `/products/${item.handle}` : ''),
      image: item.image || '',
      price: item.price || ''
    };
  }

  function getLocal() {
    try {
      const raw = JSON.parse(localStorage.getItem(LS_WL_KEY) || '[]');
      if (!Array.isArray(raw)) return [];

      return raw
        .map(normalizeItem)
        .filter(Boolean)
        .filter((item, index, array) => {
          return array.findIndex(compare => compare.gid === item.gid) === index;
        });
    } catch (error) {
      return [];
    }
  }

  function saveLocal(items) {
    const normalized = Array.isArray(items)
      ? items
          .map(normalizeItem)
          .filter(Boolean)
          .filter((item, index, array) => {
            return array.findIndex(compare => compare.gid === item.gid) === index;
          })
      : [];

    localStorage.setItem(LS_WL_KEY, JSON.stringify(normalized));

    document.dispatchEvent(new CustomEvent('wishlist:updated', {
      detail: { items: normalized }
    }));

    return normalized;
  }

  function getItemFromButton(btn) {
    const rawId = btn.dataset.productGid || btn.dataset.productId || '';
    const gid = productGid(rawId);

    return {
      id: gid,
      gid: gid,
      handle: btn.dataset.productHandle || '',
      title: btn.dataset.productTitle || '',
      url: btn.dataset.productUrl || (btn.dataset.productHandle ? `/products/${btn.dataset.productHandle}` : ''),
      image: btn.dataset.productImage || '',
      price: btn.dataset.productPrice || ''
    };
  }

  function updateButtonState(btn, inList) {
    btn.dataset.saved = inList ? 'true' : 'false';
    btn.dataset.wishlisted = inList ? 'true' : 'false';
    btn.setAttribute('aria-label', inList ? 'Remove from wishlist' : 'Add to wishlist');

    const path = btn.querySelector('svg path');
    if (path) {
      path.setAttribute('fill', inList ? 'currentColor' : 'none');
    }
  }

  function getItems() {
    return getLocal();
  }

  function isInWishlist(productId) {
    const gid = productGid(productId);
    return getLocal().some(item => item.gid === gid || item.id === gid);
  }

  function toggleFromButton(btn) {
    const clickedItem = getItemFromButton(btn);
    const items = getLocal();
    const exists = items.some(item => item.gid === clickedItem.gid);

    let next;

    if (exists) {
      next = items.filter(item => item.gid !== clickedItem.gid);
    } else {
      next = [...items, clickedItem];
    }

    saveLocal(next);

    return !exists;
  }

  function initButtons() {
    const buttons = document.querySelectorAll('[data-action="wishlist"][data-product-id]');
    if (!buttons.length) return;

    const items = getLocal();

    buttons.forEach(btn => {
      if (btn.dataset.wishlistInitialized === 'true') return;
      btn.dataset.wishlistInitialized = 'true';

      const gid = productGid(btn.dataset.productGid || btn.dataset.productId);
      const inList = items.some(item => item.gid === gid || item.id === gid);

      updateButtonState(btn, inList);

      btn.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();

        btn.disabled = true;

        try {
          const added = toggleFromButton(btn);
          const productId = btn.dataset.productId;
          const productGidValue = productGid(btn.dataset.productGid || productId);

          document
            .querySelectorAll(`[data-action="wishlist"][data-product-id="${productId}"]`)
            .forEach(matchingButton => updateButtonState(matchingButton, added));

          document
            .querySelectorAll(`[data-action="wishlist"][data-product-gid="${productGidValue}"]`)
            .forEach(matchingButton => updateButtonState(matchingButton, added));

          if (!added) {
            document.dispatchEvent(new CustomEvent('wishlist:removed', {
              detail: {
                productId: productId,
                productGid: productGidValue
              }
            }));
          }
        } finally {
          btn.disabled = false;
        }
      });
    });

    const currentItems = getLocal();
    let changed = false;

    buttons.forEach(btn => {
      const buttonItem = getItemFromButton(btn);
      const existingIndex = currentItems.findIndex(item => item.gid === buttonItem.gid);

      if (existingIndex !== -1) {
        const existingItem = currentItems[existingIndex];

        if (
          (!existingItem.handle && buttonItem.handle) ||
          (!existingItem.title && buttonItem.title) ||
          (!existingItem.url && buttonItem.url) ||
          (!existingItem.image && buttonItem.image) ||
          (!existingItem.price && buttonItem.price)
        ) {
          currentItems[existingIndex] = {
            ...existingItem,
            handle: existingItem.handle || buttonItem.handle,
            title: existingItem.title || buttonItem.title,
            url: existingItem.url || buttonItem.url,
            image: existingItem.image || buttonItem.image,
            price: existingItem.price || buttonItem.price
          };

          changed = true;
        }
      }
    });

    if (changed) {
      saveLocal(currentItems);
    }
  }

  function initWishlistPage() {
    const page = document.querySelector('[data-local-wishlist-page]');
    if (!page) return;

    const list = page.querySelector('[data-local-wishlist-list]');
    const empty = page.querySelector('[data-local-wishlist-empty]');
    const count = page.querySelector('[data-local-wishlist-count]');

    if (!list || !empty || !count) return;

    function render() {
      const items = getLocal().filter(item => item.handle || item.url);

      list.innerHTML = '';

      if (!items.length) {
        empty.hidden = false;
        list.hidden = true;
        count.textContent = '';
        return;
      }

      empty.hidden = true;
      list.hidden = false;
      count.textContent = items.length === 1 ? '1 item' : `${items.length} items`;

      items.forEach(item => {
        const card = document.createElement('div');
        card.className = 'f-column card local-wishlist-card';
        card.setAttribute('data-wishlist-card', '');
        card.setAttribute('data-product-gid', item.gid);

        const url = item.url || `/products/${item.handle}`;
        const productId = item.gid.replace('gid://shopify/Product/', '');

        card.innerHTML = `
          <div class="product-card product-card-style-standard">
            <div class="product-card__wrapper h-full">
              <div class="product-card__image-wrapper product-card__image-wrapper--main-only local-wishlist-card__image-wrapper color-scheme-1 bg-none">
                <a href="${url}" aria-label="${escapeHtml(item.title)}">
                  <div class="media-wrapper product-card__image product-card__image--main local-wishlist-card__image" style="--aspect-ratio: 1">
                    ${
                      item.image
                        ? `<img src="${escapeAttribute(item.image)}" alt="${escapeAttribute(item.title)}" loading="lazy" class="motion-reduce" width="800">`
                        : ''
                    }
                  </div>
                </a>
              </div>

              <div class="product-card__info text-center">
                <h3 class="product-card__title text-pcard-title">
                  <a class="reversed-link" href="${url}">${escapeHtml(item.title)}</a>
                </h3>

                ${item.price ? `<div class="price">${escapeHtml(item.price)}</div>` : ''}

                <button
                  type="button"
                  class="local-wishlist-remove-button"
                  aria-label="Remove from wishlist"
                  data-action="wishlist"
                  data-product-id="${escapeAttribute(productId)}"
                  data-product-gid="${escapeAttribute(item.gid)}"
                  data-product-handle="${escapeAttribute(item.handle)}"
                  data-product-title="${escapeAttribute(item.title)}"
                  data-product-url="${escapeAttribute(url)}"
                  data-product-image="${escapeAttribute(item.image)}"
                  data-product-price="${escapeAttribute(item.price)}"
                  data-saved="true"
                  data-wishlisted="true"
                >
                  <span class="local-wishlist-remove-button__icon" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="icon icon--large" focusable="false" role="presentation">
                      <path fill="currentColor" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          </div>
        `;

        list.appendChild(card);
      });

      initButtons();
    }

    render();

    document.addEventListener('wishlist:updated', render);
  }

  function escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function escapeAttribute(value) {
    return escapeHtml(value);
  }

  function init() {
    initButtons();
    initWishlistPage();
  }

  return {
    init,
    getItems,
    isInWishlist
  };
})();

document.addEventListener('DOMContentLoaded', Wishlist.init);